/**
 * Mundo Rural - Newsletter Popup
 *
 * Responsabilidades:
 * - Mostrar el popup una vez por sesión de pestaña.
 * - Persistir el cierre mediante sessionStorage.
 * - Aplicar delay configurable desde Shopify.
 * - Cerrar mediante X, overlay, "No, gracias" o Escape.
 * - Bloquear el scroll mientras el modal está abierto.
 * - Gestionar foco y navegación por teclado.
 * - Mostrar inmediatamente errores/éxito del formulario.
 * - Integrarse con Shopify Theme Editor.
 */

class MRNewsletterPopup {
  constructor(element) {
    this.popup = element;

    this.dialog = this.popup.querySelector(
      ".mr-newsletter-popup__dialog"
    );

    this.closeButtons = Array.from(
      this.popup.querySelectorAll("[data-mr-popup-close]")
    );

    this.form = this.popup.querySelector(
      ".mr-newsletter-popup__form"
    );

    this.submitButton = this.popup.querySelector(
      ".mr-newsletter-popup__submit"
    );

    this.successState = this.popup.querySelector(
      "[data-mr-popup-success]"
    );

    this.errorState = this.popup.querySelector(
      ".mr-newsletter-popup__errors"
    );

    this.delay =
      Number.parseInt(this.popup.dataset.delay, 10) || 0;

    /*
     * Una única clave para todo el sitio.
     *
     * sessionStorage desaparece al cerrar la pestaña.
     */
    this.storageKey = "mr_newsletter_popup_dismissed";

    this.isOpen = false;
    this.openTimer = null;
    this.lastFocusedElement = null;

    this.originalBodyOverflow = "";
    this.originalBodyPaddingRight = "";

    this.isShopifyEditor =
      typeof Shopify !== "undefined" &&
      Shopify.designMode;

    /*
     * Permite eliminar fácilmente todos los eventos
     * cuando Shopify vuelve a renderizar la sección.
     */
    this.abortController = new AbortController();

    this.handleKeydown =
      this.handleKeydown.bind(this);

    if (!this.dialog) {
      return;
    }

    this.init();
  }

  /**
   * Inicialización general.
   */
  init() {
    this.bindEvents();

    /*
     * Dentro del Theme Editor ignoramos sessionStorage
     * para poder visualizar y editar el popup.
     */
    if (this.isShopifyEditor) {
      this.show({
        focus: false
      });

      return;
    }

    /*
     * Si Shopify devolvió errores o éxito después
     * del POST del formulario, mostramos inmediatamente
     * el popup para que el usuario vea el resultado.
     */
    if (this.hasFormFeedback()) {
      this.show();

      return;
    }

    /*
     * Ya fue cerrado durante esta sesión.
     */
    if (this.isDismissed()) {
      return;
    }

    this.scheduleOpen();
  }

  /**
   * Registra los eventos propios del popup.
   */
  bindEvents() {
    const signal = this.abortController.signal;

    /*
     * X, overlay, "No gracias" y botón posterior
     * al mensaje de éxito utilizan data-mr-popup-close.
     */
    this.closeButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          this.close({
            persist: true
          });
        },
        { signal }
      );
    });

    /*
     * Evita envíos duplicados mientras Shopify
     * procesa la suscripción.
     *
     * No cerramos el popup aquí:
     * dejamos que Shopify procese normalmente
     * form 'customer'.
     */
    this.form?.addEventListener(
      "submit",
      () => {
        if (!this.submitButton) {
          return;
        }

        this.submitButton.disabled = true;
        this.submitButton.setAttribute(
          "aria-busy",
          "true"
        );
      },
      { signal }
    );
  }

  /**
   * Programa la apertura usando el delay
   * configurado desde Shopify.
   */
  scheduleOpen() {
    this.clearOpenTimer();

    this.openTimer = window.setTimeout(() => {
      /*
       * Comprobamos nuevamente por si el estado cambió
       * mientras esperábamos el delay.
       */
      if (!this.isDismissed()) {
        this.show();
      }
    }, this.delay);
  }

  /**
   * Muestra el popup.
   */
  show({ focus = true } = {}) {
    if (this.isOpen) {
      return;
    }

    /*
     * Fuera del editor, no debemos volver a abrir
     * algo ya descartado.
     *
     * Form feedback es la excepción.
     */
    if (
      !this.isShopifyEditor &&
      this.isDismissed() &&
      !this.hasFormFeedback()
    ) {
      return;
    }

    this.clearOpenTimer();

    this.lastFocusedElement =
      document.activeElement;

    this.popup.hidden = false;
    this.isOpen = true;

    /*
     * Clase disponible por si después queremos
     * agregar animación CSS.
     */
    requestAnimationFrame(() => {
      this.popup.classList.add("is-open");
    });

    /*
     * En el Theme Editor no bloqueamos el body
     * porque dificulta trabajar dentro del editor.
     */
    if (!this.isShopifyEditor) {
      this.lockScroll();
    }

    document.addEventListener(
      "keydown",
      this.handleKeydown
    );

    if (focus && !this.isShopifyEditor) {
      requestAnimationFrame(() => {
        this.focusInitialElement();
      });
    }
  }

  /**
   * Cierra el popup.
   */
  close({
    persist = true,
    restoreFocus = true
  } = {}) {
    this.clearOpenTimer();

    if (
      persist &&
      !this.isShopifyEditor
    ) {
      this.setDismissed();
    }

    if (!this.isOpen) {
      return;
    }

    this.popup.classList.remove("is-open");
    this.popup.hidden = true;

    this.isOpen = false;

    document.removeEventListener(
      "keydown",
      this.handleKeydown
    );

    if (!this.isShopifyEditor) {
      this.unlockScroll();
    }

    if (
      restoreFocus &&
      this.lastFocusedElement instanceof HTMLElement &&
      this.lastFocusedElement.isConnected
    ) {
      this.lastFocusedElement.focus();
    }
  }

  /**
   * Detecta si Shopify está mostrando
   * un error o un resultado exitoso.
   */
  hasFormFeedback() {
    return Boolean(
      this.successState ||
      this.errorState
    );
  }

  /**
   * Comprueba sessionStorage.
   */
  isDismissed() {
    try {
      return (
        window.sessionStorage.getItem(
          this.storageKey
        ) === "true"
      );
    } catch (error) {
      /*
       * Algunos navegadores/modos de privacidad
       * pueden bloquear storage.
       */
      return false;
    }
  }

  /**
   * Marca el popup como cerrado
   * durante la sesión actual.
   */
  setDismissed() {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        "true"
      );
    } catch (error) {
      /*
       * Si sessionStorage no está disponible,
       * simplemente no persistimos el estado.
       */
    }
  }

  /**
   * Elemento que recibirá foco
   * cuando se abre el modal.
   */
  focusInitialElement() {
    /*
     * Si acabamos de suscribirnos, priorizamos
     * el botón de continuación.
     */
    const successButton =
      this.popup.querySelector(
        ".mr-newsletter-popup__success-button"
      );

    if (successButton) {
      successButton.focus();
      return;
    }

    /*
     * En formulario normal, llevamos al primer input.
     */
    const firstInput =
      this.popup.querySelector(
        ".mr-newsletter-popup__input"
      );

    if (firstInput) {
      firstInput.focus();
      return;
    }

    /*
     * Fallback.
     */
    const closeButton =
      this.popup.querySelector(
        ".mr-newsletter-popup__close"
      );

    closeButton?.focus();
  }

  /**
   * Eventos globales de teclado.
   */
  handleKeydown(event) {
    if (!this.isOpen) {
      return;
    }

    /*
     * Escape cierra el popup.
     */
    if (event.key === "Escape") {
      event.preventDefault();

      this.close({
        persist: true
      });

      return;
    }

    /*
     * Mantiene Tab dentro del modal.
     */
    if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  /**
   * Focus trap para accesibilidad.
   */
  trapFocus(event) {
    const focusableElements = Array.from(
      this.dialog.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled]):not([type='hidden'])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          "[tabindex]:not([tabindex='-1'])"
        ].join(",")
      )
    ).filter((element) => {
      return (
        element instanceof HTMLElement &&
        !element.hidden &&
        element.offsetParent !== null
      );
    });

    if (focusableElements.length === 0) {
      return;
    }

    const firstElement =
      focusableElements[0];

    const lastElement =
      focusableElements[
        focusableElements.length - 1
      ];

    /*
     * Shift + Tab desde el primero
     * vuelve al último.
     */
    if (
      event.shiftKey &&
      document.activeElement === firstElement
    ) {
      event.preventDefault();

      lastElement.focus();

      return;
    }

    /*
     * Tab desde el último vuelve al primero.
     */
    if (
      !event.shiftKey &&
      document.activeElement === lastElement
    ) {
      event.preventDefault();

      firstElement.focus();
    }
  }

  /**
   * Bloquea el scroll sin provocar
   * un salto horizontal por la desaparición
   * de la scrollbar.
   */
  lockScroll() {
    const body = document.body;

    this.originalBodyOverflow =
      body.style.overflow;

    this.originalBodyPaddingRight =
      body.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth -
      document.documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      const currentPadding =
        Number.parseFloat(
          window
            .getComputedStyle(body)
            .paddingRight
        ) || 0;

      body.style.paddingRight =
        `${currentPadding + scrollbarWidth}px`;
    }

    body.style.overflow = "hidden";
  }

  /**
   * Recupera el scroll original.
   */
  unlockScroll() {
    const body = document.body;

    body.style.overflow =
      this.originalBodyOverflow;

    body.style.paddingRight =
      this.originalBodyPaddingRight;
  }

  /**
   * Cancela el timer pendiente.
   */
  clearOpenTimer() {
    if (!this.openTimer) {
      return;
    }

    window.clearTimeout(this.openTimer);

    this.openTimer = null;
  }

  /**
   * Limpieza para Shopify Theme Editor.
   */
  destroy() {
    this.clearOpenTimer();

    this.abortController.abort();

    document.removeEventListener(
      "keydown",
      this.handleKeydown
    );

    if (
      this.isOpen &&
      !this.isShopifyEditor
    ) {
      this.unlockScroll();
    }

    this.isOpen = false;
  }
}


/* ============================================================
   INICIALIZACIÓN
   ============================================================ */

function initMRNewsletterPopups(
  root = document
) {
  const popups = [];

  /*
   * El root puede ser directamente el popup.
   */
  if (
    root.matches?.(
      "[data-mr-newsletter-popup]"
    )
  ) {
    popups.push(root);
  }

  /*
   * O puede ser una sección que lo contiene.
   */
  if (root.querySelectorAll) {
    popups.push(
      ...root.querySelectorAll(
        "[data-mr-newsletter-popup]"
      )
    );
  }

  popups.forEach((popup) => {
    /*
     * Evita inicializar dos veces
     * el mismo componente.
     */
    if (popup.mrNewsletterPopup) {
      return;
    }

    popup.mrNewsletterPopup =
      new MRNewsletterPopup(popup);
  });
}


/* ============================================================
   CARGA NORMAL
   ============================================================ */

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      initMRNewsletterPopups();
    }
  );
} else {
  initMRNewsletterPopups();
}


/* ============================================================
   SHOPIFY THEME EDITOR
   ============================================================ */

/**
 * Shopify vuelve a renderizar una sección.
 */
document.addEventListener(
  "shopify:section:load",
  (event) => {
    initMRNewsletterPopups(
      event.target
    );
  }
);


/**
 * Shopify elimina una sección.
 */
document.addEventListener(
  "shopify:section:unload",
  (event) => {
    const popup =
      event.target.matches?.(
        "[data-mr-newsletter-popup]"
      )
        ? event.target
        : event.target.querySelector?.(
            "[data-mr-newsletter-popup]"
          );

    if (!popup?.mrNewsletterPopup) {
      return;
    }

    popup.mrNewsletterPopup.destroy();

    popup.mrNewsletterPopup = null;
  }
);


/**
 * Al seleccionar la sección en el editor,
 * abrimos el popup inmediatamente.
 */
document.addEventListener(
  "shopify:section:select",
  (event) => {
    const popup =
      event.target.matches?.(
        "[data-mr-newsletter-popup]"
      )
        ? event.target
        : event.target.querySelector?.(
            "[data-mr-newsletter-popup]"
          );

    popup?.mrNewsletterPopup?.show({
      focus: false
    });
  }
);


/**
 * Cuando dejamos de editar la sección,
 * podemos ocultarla sin guardar sessionStorage.
 */
document.addEventListener(
  "shopify:section:deselect",
  (event) => {
    const popup =
      event.target.matches?.(
        "[data-mr-newsletter-popup]"
      )
        ? event.target
        : event.target.querySelector?.(
            "[data-mr-newsletter-popup]"
          );

    if (
      !popup?.mrNewsletterPopup ||
      !popup.mrNewsletterPopup.isShopifyEditor
    ) {
      return;
    }

    popup.mrNewsletterPopup.close({
      persist: false,
      restoreFocus: false
    });
  }
);
